// All queries used in the build show page. Used by
// @tanstack/react-query

import Database from "~/lib/Database";
import { Step } from "~/lib/pipeline";
import { QueryClient } from "@tanstack/react-query";
import { Job, Build } from "~/components/build/Show/lib/types";
import { useBuildStore } from "~/components/build/Show/lib/useBuildStore";

// React Query Cache roughly looks like:
// new Map({
//   ["build", buildNumber, "steps"]: Step[],
//   ["build", buildNumber, "steps", stepUuid]: Step,
//   ["build", buildNumber, "steps", stepUuid, "jobs"]: Job[],
//   ["build", buildNumber, "jobs", jobUuid]: { job: Job, jobWithRetries: Job[] }
// });

export const fetcher = (input: RequestInfo | URL, init?: RequestInit) => {
  return fetch(input, init)
    .then((res) => {
      if (!res.ok) {
        throw new Error(res.statusText || "Failed to fetch resource");
      }

      return res.json();
    })
    .then((json) => Database.parse(json));
};

type FetchBuildStepsParams = {
  buildDataBasePath: Build["buildDataBasePath"];
  filters?: {
    state?: string;
    excludeGroupSteps?: boolean;
    depth?: "top" | "all";
    groupUuid?: string;
  };
};
type FetchBuildStepsResponse = Step[];

/**
 * Fetches the steps for a build optionally filtered by state.
 */
export async function fetchBuildSteps({
  buildDataBasePath,
  filters,
}: FetchBuildStepsParams): Promise<FetchBuildStepsResponse> {
  const url = new URL(`${buildDataBasePath}/steps`, window.location.origin);

  // Note: The order of the parameters matters since rails will automatically order search params alphabetically.
  // This means in order to prefetch this query we need to ensure that the parameters are in the same order.

  if (filters && filters.depth !== undefined) {
    url.searchParams.append("depth", String(filters.depth));
  }

  if (filters && filters.excludeGroupSteps !== undefined) {
    url.searchParams.append("exclude_group_steps", String(filters.excludeGroupSteps));
  }

  if (filters && filters.groupUuid !== undefined) {
    url.searchParams.append("group_uuid", filters.groupUuid);
  }

  if (filters?.state) {
    url.searchParams.append("state", filters.state);
  }

  return fetcher(url);
}

type FetchBuildStepResponse = { step: Step };
export async function fetchBuildStep(
  buildDataBasePath: string,
  stepUuid: string,
): Promise<FetchBuildStepResponse> {
  return fetcher(`${buildDataBasePath}/steps/${stepUuid}`);
}

type FetchBuildStepWithJobsParams = {
  buildDataBasePath: Build["buildDataBasePath"];
  stepUuid: string;
  buildNumber: Build["number"];
  queryClient: QueryClient;
};

type FetchBuildStepWithJobsResponse = { step: Step; jobs: Job[] };
export async function fetchBuildStepWithJobs({
  buildDataBasePath,
  stepUuid,
  buildNumber,
  queryClient,
}: FetchBuildStepWithJobsParams): Promise<FetchBuildStepWithJobsResponse> {
  return fetcher(`${buildDataBasePath}/steps/${stepUuid}?include_jobs=true`).then(
    (data: FetchBuildStepWithJobsResponse) => {
      queryClient.setQueryData(["build", buildNumber, "steps", stepUuid], data.step);
      queryClient.setQueryData(["build", buildNumber, "steps", stepUuid, "jobs"], data.jobs);
      return data;
    },
  );
}

type FetchBuildJobsParams = {
  afterCursor?: string;
  buildDataBasePath: Build["buildDataBasePath"];
};
type FetchBuildJobsResponse = {
  records: Job[];
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  startCursor: string;
  endCursor: string;
};
export async function fetchBuildJobs({
  afterCursor,
  buildDataBasePath,
}: FetchBuildJobsParams): Promise<FetchBuildJobsResponse> {
  const params = new URLSearchParams();
  if (afterCursor) {
    params.set("after", afterCursor);
  }

  return fetcher(`${buildDataBasePath}/jobs?${params.toString()}`);
}

type FetchBuildStepJobsParams = {
  buildDataBasePath: Build["buildDataBasePath"];
  stepUuid: string;
};
type FetchBuildStepJobsResponse<T extends Job> = T[];

export async function fetchBuildStepJobs<T extends Job>({
  buildDataBasePath,
  stepUuid,
}: FetchBuildStepJobsParams): Promise<FetchBuildStepJobsResponse<T>> {
  return fetcher(`${buildDataBasePath}/steps/${stepUuid}/jobs`);
}

type FetchBuildJobParams = {
  buildDataBasePath: Build["buildDataBasePath"];
  jobUuid: string;
  buildNumber: Build["number"];
  queryClient: QueryClient;
};
type FetchBuildJobResponse = { job: Job; jobWithRetries: Job[] };
export async function fetchBuildJob({
  buildDataBasePath,
  jobUuid,
  buildNumber,
  queryClient,
}: FetchBuildJobParams): Promise<FetchBuildJobResponse> {
  return fetcher(`${buildDataBasePath}/jobs/${jobUuid}?include_retries=true`).then(
    (data: FetchBuildJobResponse) => {
      data.jobWithRetries.forEach((job: Job) => {
        if (job.id !== jobUuid) {
          // Update store with latest version of jobs
          useBuildStore.getState().setJobs([job, ...data.jobWithRetries]);

          queryClient.setQueryData(["build", buildNumber, "jobs", job.id], {
            job,
            jobWithRetries: data.jobWithRetries,
          });
        }
      });
      return data;
    },
  );
}

type FetchJobArtifactsCountParams = {
  jobBasePath: string;
};
type FetchJobArtifactsCountResponse = number;
export async function fetchJobArtifactsCount({
  jobBasePath,
}: FetchJobArtifactsCountParams): Promise<FetchJobArtifactsCountResponse> {
  return fetcher(`${jobBasePath}/artifacts/count`);
}
